/**
 * =====================================================================
 * TEMPLATE EDIT MODAL - Admin Dashboard Component
 * Version: V4.52
 * Purpose: Edit or create PDF templates with code editors
 * =====================================================================
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    MenuItem,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import {
    Save as SaveIcon,
    Close as CloseIcon,
    Code as CodeIcon,
} from '@mui/icons-material';
import apiService from '../../services/api';

interface Template {
    id?: string;
    template_key: string;
    template_type: 'chapter' | 'book';
    display_name: string;
    display_description: string;
    html_template: string;
    css_styles: string;
    template_variables: any;
    preview_image_url: string | null;
    is_active: boolean;
    sort_order: number;
    is_premium: boolean;
}

interface Props {
    open: boolean;
    template: any | null;
    isCreatingNew: boolean;
    onClose: () => void;
    onSave: () => void;
}

const TemplateEditModal: React.FC<Props> = ({ open, template, isCreatingNew, onClose, onSave }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Form state
    const [formData, setFormData] = useState<Template>({
        template_key: '',
        template_type: 'chapter',
        display_name: '',
        display_description: '',
        html_template: '',
        css_styles: '',
        template_variables: '{}',
        preview_image_url: null,
        is_active: true,
        sort_order: 0,
        is_premium: false,
    });

    // =====================================================================
    // LOAD TEMPLATE DATA
    // =====================================================================

    useEffect(() => {
        if (isCreatingNew) {
            // Reset to empty form
            setFormData({
                template_key: '',
                template_type: 'chapter',
                display_name: '',
                display_description: '',
                html_template: DEFAULT_HTML_TEMPLATE,
                css_styles: DEFAULT_CSS_STYLES,
                template_variables: '{}',
                preview_image_url: null,
                is_active: true,
                sort_order: 0,
                is_premium: false,
            });
        } else if (template) {
            // Load existing template - need to fetch full details including HTML/CSS
            loadTemplateDetails(template.id);
        }
    }, [template, isCreatingNew]);

    const loadTemplateDetails = async (templateId: string) => {
        try {
            setLoading(true);
            const response = await apiService.get(`/api/admin/templates/${templateId}`);

            if (response.data.success) {
                const t = response.data.template;
                setFormData({
                    id: t.id,
                    template_key: t.template_key,
                    template_type: t.template_type,
                    display_name: t.display_name,
                    display_description: t.display_description || '',
                    html_template: t.html_template,
                    css_styles: t.css_styles,
                    template_variables: typeof t.template_variables === 'string'
                        ? t.template_variables
                        : JSON.stringify(t.template_variables, null, 2),
                    preview_image_url: t.preview_image_url,
                    is_active: t.is_active,
                    sort_order: t.sort_order,
                    is_premium: t.is_premium,
                });
            }
        } catch (err: any) {
            console.error('Error loading template details:', err);
            setError('Failed to load template details');
        } finally {
            setLoading(false);
        }
    };

    // =====================================================================
    // FORM HANDLERS
    // =====================================================================

    const handleChange = (field: keyof Template, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            setError(null);

            // Validate required fields
            if (!formData.template_key || !formData.display_name || !formData.html_template || !formData.css_styles) {
                setError('Please fill in all required fields');
                return;
            }

            // Parse template_variables if it's a string
            let templateVariables: any = {};
            if (typeof formData.template_variables === 'string') {
                try {
                    templateVariables = JSON.parse(formData.template_variables);
                } catch {
                    setError('Invalid JSON in Template Variables field');
                    return;
                }
            } else {
                templateVariables = formData.template_variables;
            }

            const payload = {
                ...formData,
                template_variables: templateVariables,
            };

            if (isCreatingNew) {
                // CREATE
                await apiService.post('/api/admin/templates', payload);
            } else {
                // UPDATE
                await apiService.put(`/api/admin/templates/${formData.id}`, payload);
            }

            onSave(); // Refresh parent list
            onClose();
        } catch (err: any) {
            console.error('Error saving template:', err);
            setError(err.message || 'Failed to save template');
        } finally {
            setLoading(false);
        }
    };

    // =====================================================================
    // RENDER
    // =====================================================================

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                sx: { height: '90vh' },
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5">
                        {isCreatingNew ? 'Create New Template' : `Edit Template: ${formData.display_name}`}
                    </Typography>
                    <Button startIcon={<CloseIcon />} onClick={onClose}>
                        Close
                    </Button>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {error && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                )}

                {/* Tabs */}
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
                    <Tab label="Basic Info" />
                    <Tab label="HTML Template" />
                    <Tab label="CSS Styles" />
                    <Tab label="Variables" />
                </Tabs>

                {/* TAB 1: Basic Info */}
                {activeTab === 0 && (
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                required
                                label="Template Key"
                                placeholder="e.g., modern_family_chapter"
                                value={formData.template_key}
                                onChange={(e) => handleChange('template_key', e.target.value)}
                                disabled={!isCreatingNew}
                                helperText="Unique identifier (cannot be changed after creation)"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                required
                                select
                                label="Template Type"
                                value={formData.template_type}
                                onChange={(e) => handleChange('template_type', e.target.value)}
                                disabled={!isCreatingNew}
                            >
                                <MenuItem value="chapter">Chapter</MenuItem>
                                <MenuItem value="book">Book</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                required
                                label="Display Name"
                                placeholder="e.g., Modern Family Chapter"
                                value={formData.display_name}
                                onChange={(e) => handleChange('display_name', e.target.value)}
                                helperText="Name shown to users"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Display Description"
                                placeholder="Brief description shown to users..."
                                value={formData.display_description}
                                onChange={(e) => handleChange('display_description', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Preview Image URL"
                                placeholder="https://example.com/preview.png"
                                value={formData.preview_image_url || ''}
                                onChange={(e) => handleChange('preview_image_url', e.target.value || null)}
                                helperText="Optional: S3 URL to preview image"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Sort Order"
                                value={formData.sort_order}
                                onChange={(e) => handleChange('sort_order', parseInt(e.target.value))}
                                helperText="Display order (lower numbers appear first)"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.is_active}
                                        onChange={(e) => handleChange('is_active', e.target.checked)}
                                    />
                                }
                                label="Active (visible to users)"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.is_premium}
                                        onChange={(e) => handleChange('is_premium', e.target.checked)}
                                    />
                                }
                                label="Premium Template"
                            />
                        </Grid>
                    </Grid>
                )}

                {/* TAB 2: HTML Template */}
                {activeTab === 1 && (
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Use Handlebars syntax: <code>{'{{variable}}'}</code>, <code>{'{{#if condition}}'}</code>, <code>{'{{#each array}}'}</code>
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            required
                            rows={25}
                            label="HTML Template"
                            value={formData.html_template}
                            onChange={(e) => handleChange('html_template', e.target.value)}
                            sx={{
                                '& textarea': {
                                    fontFamily: 'monospace',
                                    fontSize: '14px',
                                },
                            }}
                        />
                    </Box>
                )}

                {/* TAB 3: CSS Styles */}
                {activeTab === 2 && (
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Include all CSS needed for the template. Use <code>@page</code> for print settings.
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            required
                            rows={25}
                            label="CSS Styles"
                            value={formData.css_styles}
                            onChange={(e) => handleChange('css_styles', e.target.value)}
                            sx={{
                                '& textarea': {
                                    fontFamily: 'monospace',
                                    fontSize: '14px',
                                },
                            }}
                        />
                    </Box>
                )}

                {/* TAB 4: Template Variables */}
                {activeTab === 3 && (
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Define available variables as JSON (for documentation purposes).
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={25}
                            label="Template Variables (JSON)"
                            value={formData.template_variables}
                            onChange={(e) => handleChange('template_variables', e.target.value)}
                            placeholder='{"chapter_title": "string", "chapter_number": "integer"}'
                            sx={{
                                '& textarea': {
                                    fontFamily: 'monospace',
                                    fontSize: '14px',
                                },
                            }}
                            helperText="Document the variables used in this template (stored as JSONB)"
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={loading}
                >
                    {loading ? 'Saving...' : (isCreatingNew ? 'Create Template' : 'Save Changes')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// =====================================================================
// DEFAULT TEMPLATES FOR NEW CREATION
// =====================================================================

const DEFAULT_HTML_TEMPLATE = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>{{chapter_title}}</title>
</head>
<body>
    <div class="page-container">
        <header class="chapter-header">
            <h1 class="chapter-title">{{chapter_title}}</h1>
            {{#if date}}
            <p class="chapter-date">{{date}}</p>
            {{/if}}
        </header>

        <main class="chapter-content">
            <div class="chapter-text">{{chapter_content}}</div>
        </main>

        <footer class="chapter-footer">
            <p class="page-number">{{page_number}}</p>
        </footer>
    </div>
</body>
</html>`;

const DEFAULT_CSS_STYLES = `@page {
    size: 8.5in 11in;
    margin: 1in;
}

* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

body {
    font-family: "Georgia", "Times New Roman", serif;
    font-size: 18pt;
    line-height: 2.0;
    color: #1a1a1a;
    background: #ffffff;
}

.page-container {
    width: 100%;
    min-height: 100vh;
    padding: 20px;
}

.chapter-header {
    text-align: center;
    margin-bottom: 40px;
}

.chapter-title {
    font-size: 36pt;
    font-weight: 700;
    margin-bottom: 10px;
}

.chapter-date {
    font-size: 16pt;
    color: #666;
}

.chapter-content {
    margin-bottom: 40px;
}

.chapter-text {
    font-size: 18pt;
    line-height: 2.0;
    text-align: justify;
}

.chapter-footer {
    text-align: center;
    margin-top: 40px;
}

.page-number {
    font-size: 16pt;
    color: #666;
}`;

export default TemplateEditModal;
